import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import "./inspection-form.css";
import { useDispatch, useSelector } from "react-redux";
import Header from "../../../components/header/header";
import Inspection from "./inspection/inspection";
import {
  getOrderDetailsRequest,
  getOrderInspectionRequest,
  getOrderInspectionSuccess,
  getOrderIssuesCountRequest,
  getOrderIssuesCountSuccess,
  getQCModificationRequest,
  getQCModificationSuccess,
} from "../../../redux/orders/slice";
import {
  getOrderStatusColor,
  orderStatus,
  userType,
} from "../../../utils/Constant";
import DoneOutlineOutlinedIcon from "@mui/icons-material/DoneOutlineOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import OrderIssues from "./issues/issues";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ErrorNotification from "../../../components/error-notification/error-notification";
import Loading from "../../../components/loading/loading";
import {
  getQCStatusRequest,
  inspectorReturnRequest,
  submitInspectionForm,
  submitQCInspectionForm,
  updateQCStatusRequest,
} from "../../../utils/APICalls";
import QCReviewInspection from "./qc-review/qc-review";
import eventBus from "../../../utils/EventBus";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import ModifyInspection from "./modification/modification";
import { currentUserRequest } from "../../../redux/users/slice";
import Attachments from "./attachments/attachments";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function TabLabel(data: any) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {data?.label}
      {data?.chipText && (
        <Chip
          label={data.chipText}
          size="small"
          sx={{
            ml: 1,
            backgroundColor: data?.isSelected ? "primary.main" : "#00000099",
            color: "white",
            height: 20,
            fontSize: 12,
            fontWeight: 400,
            "& .MuiChip-label": {
              px: 1,
            },
          }}
        />
      )}
    </Box>
  );
}

export default function InspectionForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [value, setValue] = useState(0);
  const [isRequiredAnswered, setIsRequiredAnswered] = useState(false);
  const [isAllAnsweredApproved, setIsAllAnsweredApproved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSecondQCRequired, setIsSecondQCRequired] = useState(false);
  const [qc1Completed, setQC1Completed] = useState(false);
  const [qc2Completed, setQC2Completed] = useState(false);
  const [issuesCount, setIssuesCount] = useState<{
    openIssuesCount: number;
    totalIssuesCount: number;
  } | null>(null);
  const [qc1Info, setQC1Info] = useState<{ name: string; date: string } | null>(
    null
  );
  const [qc2Info, setQC2Info] = useState<{ name: string; date: string } | null>(
    null
  );
  const [isQCModificationValid, setIsQCModificationValid] = useState(false);
  const [showRequiredField, setShowRequiredField] = useState(false);

  const { orderDetails, orderInspection, orderIssuesCount, qcModification } =
    useSelector((state: any) => state.orders);
  const { currentUser } = useSelector((state: any) => state.user.user);

  useEffect(() => {
    if (!orderDetails && state) {
      dispatch(getOrderDetailsRequest({ id: state?.orderId }));
    }
    dispatch(getOrderInspectionSuccess({ response: null }));
    dispatch(getOrderInspectionRequest({ orderId: state?.orderId }));
    dispatch(getOrderIssuesCountSuccess({ response: null }));
    dispatch(getOrderIssuesCountRequest({ orderId: state.orderId }));
    dispatch(getQCModificationSuccess({ response: null }));
    dispatch(getQCModificationRequest({ orderId: state.orderId }));
    setShowRequiredField(false);
  }, [state]); // eslint-disable-line

  useEffect(() => {
    if (!currentUser) {
      dispatch(currentUserRequest());
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (orderIssuesCount) {
      setIssuesCount({
        openIssuesCount: orderIssuesCount.openIssuesCount || 0,
        totalIssuesCount: orderIssuesCount.totalIssuesCount || 0,
      });
    }
  }, [orderIssuesCount]);

  useEffect(() => {
    if (orderInspection) {
      const requireSecondQC =
        orderInspection?.qcDetails?.requireSecondQC || false;
      setIsSecondQCRequired(requireSecondQC);
      if (requireSecondQC) {
        getQCStatus();
      }
    }
  }, [orderInspection]); // eslint-disable-line

  useEffect(() => {
    if (qcModification) {
      if (qcModification?.isQcModificationRequired) {
        if (qcModification?.fileName) {
          setIsQCModificationValid(true);
        } else {
          setIsQCModificationValid(false);
        }
      } else {
        setIsQCModificationValid(true);
      }
    }
  }, [qcModification]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const checkAnswers = (isAnswered: any) => {
    setIsRequiredAnswered(isAnswered);
  };

  const checkApproved = (isApproved: boolean) => {
    setIsAllAnsweredApproved(isApproved);
  };

  const submitForm = async () => {
    setIsLoading(true);
    const response = await submitInspectionForm(state?.orderId);
    setIsLoading(false);
    if (response?.status === 204 || response?.status === 200) {
      dispatch(getOrderDetailsRequest({ id: state?.orderId }));
      setTimeout(() => {
        eventBus.emit("inspectionSuccess", { isReview: false });
      }, 100);
      navigate(-1);
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const submitQCReviewForm = async () => {
    setIsLoading(true);
    const response = await submitQCInspectionForm(state?.orderId);
    setIsLoading(false);
    if (response?.status === 204 || response?.status === 200) {
      dispatch(getOrderDetailsRequest({ id: state?.orderId }));
      setTimeout(() => {
        eventBus.emit("inspectionSuccess", { isReview: true });
      }, 100);
      navigate(-1);
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const returnToInspector = async () => {
    setIsLoading(true);
    const response = await inspectorReturnRequest(state?.orderId);
    setIsLoading(false);
    if (response?.status === 204 || response?.status === 200) {
      dispatch(getOrderDetailsRequest({ id: state?.orderId }));
      setTimeout(() => {
        eventBus.emit("inspectionSuccess", {
          message: "Order Returned to Inspector Successfully.",
        });
      }, 100);
      navigate(-1);
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const getQCStatus = async () => {
    setIsLoading(true);
    const response = await getQCStatusRequest(state?.orderId);
    setIsLoading(false);
    if (response?.status === 200) {
      setQC1Completed(response?.data?.qcCompleted || false);
      setQC2Completed(response?.data?.qc2Completed || false);
      setQC1Info({
        name: response?.data?.qcUser?.name,
        date: response?.data?.qcCompletedAt,
      });
      setQC2Info({
        name: response?.data?.qc2User?.name,
        date: response?.data?.qc2CompletedAt,
      });
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const updateQC1Status = async () => {
    setIsLoading(true);
    const response = await updateQCStatusRequest(state?.orderId, false);
    setIsLoading(false);
    if (response?.status === 204) {
      setQC1Completed(true);
      dispatch(getOrderDetailsRequest({ id: state?.orderId }));
      getQCStatus();
    } else if (response?.status === 400 || response?.status === 403) {
      setQC1Completed(false);
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const updateQC2Status = async (checked: boolean) => {
    setIsLoading(true);
    const response = await updateQCStatusRequest(state?.orderId, true, checked);
    setIsLoading(false);
    if (response?.status === 204) {
      setQC2Completed(true);
      dispatch(getOrderDetailsRequest({ id: state?.orderId }));
      getQCStatus();
    } else if (response?.status === 400 || response?.status === 403) {
      setQC2Completed(false);
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <>
      <Header />
      <Box className="container">
        <Box sx={{ mx: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              sx={{ mt: 2 }}
              variant="text"
              startIcon={<ArrowBackOutlinedIcon color="primary" />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            {state?.isQCReview ? (
              <Box sx={{ mt: 2, display: "flex", alignItems: "flex-start" }}>
                {!isAllAnsweredApproved && (
                  <Button
                    sx={{ mt: 1, mr: 2 }}
                    variant="outlined"
                    onClick={returnToInspector}
                  >
                    Return to Inspector
                  </Button>
                )}
                <Box mt={1}>
                  {isAllAnsweredApproved ? (
                    <Chip
                      icon={<CheckCircleOutlinedIcon />}
                      sx={{ mr: 2, height: 36 }}
                      variant="filled"
                      label={"All Questions Approved"}
                      size="small"
                      color="success"
                    />
                  ) : (
                    <Chip
                      sx={{ mr: 2, height: 36 }}
                      variant="outlined"
                      label={"Review Incomplete"}
                      size="small"
                    />
                  )}
                </Box>
                {isSecondQCRequired && (
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={qc1Completed}
                          disabled={qc1Completed}
                          onChange={(e) => {
                            if (e.target.checked) {
                              updateQC1Status();
                            }
                          }}
                        />
                      }
                      label={
                        <Box display={"flex"} alignItems={"center"}>
                          <Typography>QC 1 Complete</Typography>
                          {qc1Completed && (
                            <Tooltip
                              title={`${dayjs(qc1Info?.date).format(
                                "MMM D, YYYY"
                              )} by ${qc1Info?.name}`}
                              arrow
                              placement="top"
                            >
                              <IconButton sx={{ p: 0 }}>
                                <InfoOutlinedIcon
                                  sx={{ ml: 1 }}
                                  color="primary"
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={qc2Completed}
                          disabled={!isAllAnsweredApproved || !qc1Completed}
                          onChange={(e) => {
                            updateQC2Status(e.target.checked);
                          }}
                        />
                      }
                      label={
                        <Box display={"flex"} alignItems={"center"}>
                          <Typography>QC 2 Complete</Typography>
                          {qc2Completed && (
                            <Tooltip
                              title={`${dayjs(qc2Info?.date).format(
                                "MMM D, YYYY"
                              )} by ${qc2Info?.name}`}
                              arrow
                              placement="top"
                            >
                              <IconButton sx={{ p: 0 }}>
                                <InfoOutlinedIcon
                                  sx={{ ml: 1 }}
                                  color="primary"
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      }
                    />
                  </FormGroup>
                )}
                {!isSecondQCRequired && (
                  <Button
                    sx={{ mt: 1 }}
                    variant="contained"
                    startIcon={<DoneOutlineOutlinedIcon />}
                    disabled={!isAllAnsweredApproved || !isQCModificationValid}
                    onClick={submitQCReviewForm}
                  >
                    Submit
                  </Button>
                )}
                {isSecondQCRequired && qc1Completed && (
                  <Button
                    sx={{ mt: 1 }}
                    variant="contained"
                    startIcon={<DoneOutlineOutlinedIcon />}
                    disabled={!qc2Completed || !isQCModificationValid}
                    onClick={submitQCReviewForm}
                  >
                    Submit
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                {isRequiredAnswered ? (
                  <Chip
                    icon={<CheckCircleOutlinedIcon />}
                    sx={{ mr: 2, height: 36 }}
                    variant="filled"
                    label={"All Required Questions Completed"}
                    size="small"
                    color="success"
                  />
                ) : (
                  <Chip
                    sx={{ mr: 2, height: 36 }}
                    variant="outlined"
                    label={"Required Questions Incomplete"}
                    size="small"
                    onClick={() => setShowRequiredField(true)}
                  />
                )}

                <Button
                  variant="contained"
                  startIcon={<DoneOutlineOutlinedIcon />}
                  disabled={
                    !(
                      orderStatus[
                        orderDetails?.orderStatus as keyof typeof orderStatus
                      ] >= 41 &&
                      orderStatus[
                        orderDetails?.orderStatus as keyof typeof orderStatus
                      ] < 51
                    ) || !isRequiredAnswered
                  }
                  onClick={submitForm}
                >
                  Submit
                </Button>
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h4">
              Order #{orderDetails?.orderNumber}
            </Typography>
            <Button
              sx={{ ml: 2 }}
              startIcon={<OpenInNewOutlinedIcon />}
              onClick={() => navigate(-1)}
            >
              Order Details
            </Button>
            {!state?.isQCReview &&
              currentUser?.userType !== userType.inspector && (
                <Button sx={{ ml: 1 }} startIcon={<PrintOutlinedIcon />}>
                  Print Inspection Form
                </Button>
              )}
          </Box>
          <Box sx={{ mt: 1.5, display: "flex", alignItems: "center" }}>
            {state?.isQCReview ? (
              <>
                <Avatar
                  alt="Avatar"
                  variant="circular"
                  sx={{ width: 24, height: 24 }}
                  src={orderDetails?.client?.logoUrl}
                ></Avatar>
                <Typography sx={{ ml: 0.5 }}>
                  {orderDetails?.client?.clientName || "-"}
                </Typography>
                {orderDetails?.inspectorFirm ? (
                  <Box sx={{ ml: 3, display: "flex", alignItems: "center" }}>
                    <Avatar
                      alt="Avatar"
                      variant="circular"
                      sx={{ width: 24, height: 24 }}
                      src={orderDetails?.inspectorFirm?.logoUrl}
                    ></Avatar>
                    <Typography sx={{ ml: 0.5 }}>
                      {orderDetails?.inspectorFirm?.displayName}
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ ml: 1 }}>No Inspector Assigned</Typography>
                )}
              </>
            ) : (
              <>
                {orderDetails?.inspectorFirm ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      alt="Avatar"
                      variant="circular"
                      sx={{ width: 24, height: 24 }}
                      src={orderDetails?.inspectorFirm?.logoUrl}
                    ></Avatar>
                    <Typography sx={{ ml: 0.5 }}>
                      {orderDetails?.inspectorFirm?.displayName}
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ ml: 1 }}>No Inspector Assigned</Typography>
                )}
              </>
            )}

            {orderDetails?.orderStatus && (
              <Chip
                label={orderDetails?.orderStatus}
                size="small"
                sx={{
                  ml: 3,
                  backgroundColor: getOrderStatusColor(
                    orderDetails?.orderStatus
                  ),
                  color: "white",
                }}
              />
            )}
          </Box>
        </Box>

        <Box>
          {state?.isQCReview ? (
            <Tabs
              sx={{ m: 2 }}
              value={value}
              onChange={handleChange}
              aria-label="form builder tabs"
            >
              <Tab
                className="tab-label"
                label={<TabLabel label="QC Review" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="Modifications" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={
                  <TabLabel
                    label="Issues"
                    showWarning={false}
                    isSelected={value === 2}
                    chipText={
                      issuesCount && issuesCount.totalIssuesCount > 0
                        ? `${issuesCount.openIssuesCount} of ${issuesCount.totalIssuesCount}`
                        : undefined
                    }
                  />
                }
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="Attachments" showWarning={false} />}
              />
            </Tabs>
          ) : (
            <Tabs
              sx={{ m: 2 }}
              value={value}
              onChange={handleChange}
              aria-label="form builder tabs"
            >
              <Tab
                className="tab-label"
                label={<TabLabel label="Inspection" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={
                  <TabLabel
                    label="Issues"
                    showWarning={false}
                    isSelected={value === 1}
                    chipText={
                      issuesCount && issuesCount.totalIssuesCount > 0
                        ? `${issuesCount.openIssuesCount} of ${issuesCount.totalIssuesCount}`
                        : undefined
                    }
                  />
                }
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="Attachments" showWarning={false} />}
              />
            </Tabs>
          )}
        </Box>
      </Box>

      {state?.isQCReview ? (
        <>
          <CustomTabPanel value={value} index={0}>
            <QCReviewInspection
              data={orderDetails}
              checkApproved={checkApproved}
              qc2Completed={qc2Completed}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <ModifyInspection
              orderId={orderDetails?.id}
              orderNumber={orderDetails?.orderNumber}
              isAllAnsweredApproved={isAllAnsweredApproved}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <OrderIssues orderId={orderDetails?.id} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            <Attachments orderId={orderDetails?.id} />
          </CustomTabPanel>
        </>
      ) : (
        <>
          <CustomTabPanel value={value} index={0}>
            <Inspection
              data={orderDetails}
              checkAnswers={checkAnswers}
              showRequiredField={showRequiredField}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <OrderIssues orderId={orderDetails?.id} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <Attachments orderId={orderDetails?.id} />
          </CustomTabPanel>
        </>
      )}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {isLoading && <Loading />}
    </>
  );
}
